/**
 * Помощник работы с массивами
 */
export class ArrayHelper
{
    /**
     * Сортировка массива объектов по свойству на увеличение
     * @param {object[] | []} arrayOfObjects массив объектов, который необходимо отсортировать
     * @param {string} attributeName наименование аттрибута объектов
     * @returns {object[] | []}
     */
    static sortObjectsByNumericAttrAsc(arrayOfObjects, attributeName)
    {
        return arrayOfObjects.sort(( a, b ) => a[attributeName] - b[attributeName]);
    }

    /**
     * Сортировка массива объектов по свойству на уменьшение
     * @param {object[] | []} arrayOfObjects массив объектов, который необходимо отсортировать
     * @param {string} attributeName наименование аттрибута объектов
     * @returns {object[] | []}
     */
    static sortObjectsByNumericAttrDesc(arrayOfObjects, attributeName)
    {
        return arrayOfObjects.sort(( a, b ) => b[attributeName] - a[attributeName]);
    }

    /**
     * Получаем массив всех значений определенного свойства всех объектов
     * @param {object[] | []} arrayOfObjects массив объектов
     * @param {string} attributeName наименование аттрибута объектов
     * @return {[]}
     */
    static getAttrValuesOfObjectsByName(arrayOfObjects, attributeName)
    {
        return arrayOfObjects.map(item => {
            if (!item.hasOwnProperty(attributeName)) {
                return;
            }

            return item[attributeName];
        });
    }

    /**
     * Получаем массив уникальных значений определенного свойства всех объектов
     * @param {object[] | []} arrayOfObjects массив объектов
     * @param {string} attributeName наименование аттрибута объектов
     * @return {[]}
     */
    static getUniqueAttrValuesOfObjectsByName(arrayOfObjects, attributeName)
    {
        // получаем все значения свойства объектов
        const data = this.getAttrValuesOfObjectsByName(arrayOfObjects, attributeName);

        // возвращаем только уникальные значения
        return [...new Set(data)];
    }

    /**
     * Проверяем наличие объекта в массиве объектов по значению свойства
     * @param {object[] | []} arrayOfObjects
     * @param {string} attributeName
     * @param {string | number} attributeValue
     * @return {boolean}
     */
    static hasObjectByAttrValue(arrayOfObjects, attributeName, attributeValue)
    {
        if (arrayOfObjects.length === 0) {
            return false;
        }

        const data = this.getUniqueAttrValuesOfObjectsByName(arrayOfObjects, attributeName);

        return data.includes(attributeValue);
    }

    /**
     * Получаем объект с массива объектов по значению свойства
     * @param {object[] | []} arrayOfObjects
     * @param {string} attributeName
     * @param {string | number} attributeValue
     * @return {object | null}
     */
    static getObjectByAttrValue(arrayOfObjects, attributeName, attributeValue)
    {
        return arrayOfObjects.find(
            item => (item.hasOwnProperty(attributeName) && item[attributeName] === attributeValue)
        );
    }

    /**
     * Получаем все объекты с массива объектов по значению свойства
     * @param {object[] | []} arrayOfObjects
     * @param {string} attributeName
     * @param {any} attributeValue
     * @return {object[] | []}
     */
    static getObjectsByAttrValue(arrayOfObjects, attributeName, attributeValue)
    {
        return arrayOfObjects.filter(
            item => (item.hasOwnProperty(attributeName) && item[attributeName] === attributeValue)
        );
    }

    /**
     * Получаем все объекты с массива объектов по исключению значения свойства
     * @param {object[] | []} arrayOfObjects
     * @param {string} attributeName
     * @param {string | number} attributeValue
     * @return {object[] | []}
     */
    static getObjectsByExcludeAttrValue(arrayOfObjects, attributeName, attributeValue)
    {
        return arrayOfObjects.filter(
            item => (item.hasOwnProperty(attributeName) && item[attributeName] !== attributeValue)
        );
    }

    /**
     * Удаляем объект из массива объектов по значению свойства
     * @param {object[] | []} arrayOfObjects
     * @param {string} attributeName
     * @param {string | number} attributeValue
     */
    static removeObjectByAttrValue(arrayOfObjects, attributeName, attributeValue)
    {
        return arrayOfObjects.filter(item => (!item.hasOwnProperty(attributeName) || item[attributeName] !== attributeValue));
    }

    /**
     * Получаем массив уникальных объектов по свойству
     * @param {object[] | []} arrayOfObjects
     * @param {string} attributeName
     * @return {object[] | []}
     */
    static uniqueObjectsByAttrName(arrayOfObjects, attributeName)
    {
        const newArray = new Map();

        arrayOfObjects.forEach(item => {
            const propertyValue = item[attributeName];
            newArray.has(propertyValue)
                ? newArray.set(propertyValue, { ...item, ...newArray.get(propertyValue) })
                : newArray.set(propertyValue, item);
        });

        return Array.from(newArray.values());
    }

    /**
     * Получаем уникальные объекты первого массива, исключая из второго по значению свойства
     * @param {object[] | []} firstArrayOfObject
     * @param {object[] | []} secondArrayOfObject
     * @param {string} attributeName
     * @return {object[] | []}
     */
    static getLeftDiffObjectsByAttrName(firstArrayOfObject, secondArrayOfObject, attributeName)
    {
        return firstArrayOfObject.filter(firstObject => {
            return !secondArrayOfObject.some(secondObject => {
                if (!firstObject.hasOwnProperty(attributeName) || !secondObject.hasOwnProperty(attributeName)) {
                    return true;
                }

                return firstObject[attributeName] === secondObject[attributeName];
            });
        });
    }

    /**
     * Получаем родительские объекты по значению свойства
     * @param {object[] | []} arrayObjects
     * @param {string | number} keyValue значение ключа
     * @param {string} keyName название свойства, по которому ищем
     * @param {string} keyChildName название свойства, в котором есть дочерние элементы
     * @return {{}[] | []}
     */
    static getParentsObjectsByParamValue(arrayObjects, keyValue, keyName, keyChildName)
    {
        const isFoundChild = (id, data, parents) => {
            if (data.find(item => item[keyName] === keyValue)) {
                return true;
            } else {
                for (let item of data) {
                    if (item[keyChildName] && item[keyChildName].length) {
                        if (isFoundChild(keyValue, item[keyChildName])) {
                            parents.push(item);
                            return true;
                        }
                    }
                }
                return false;
            }
        }

        const parents = [];

        if (arrayObjects.find(item => item[keyName] === keyValue)) {
            return [];
        } else {
            for (let item of arrayObjects) {
                if (item[keyChildName] && item[keyChildName].length) {
                    if (isFoundChild(keyValue, item[keyChildName], parents)) {
                        parents.push(item);
                        return parents;
                    }
                }
            }
        }

        return parents;
    }

    /**
     * Получаем родительские объекты, которые начинаются на значение свойства
     * @param {object[] | []} arrayObjects
     * @param {string | number} keyValue значение ключа
     * @param {string} keyName название свойства, по которому ищем
     * @param {string} keyChildName название свойства, в котором есть дочерние элементы
     * @return {{}[] | []}
     */
    static getParentsObjectsByStartWithParamValue(arrayObjects, keyValue, keyName, keyChildName)
    {
        const isFoundChild = (id, data, parents) => {
            if (data.find(item => keyValue.startsWith(item[keyName]))) {
                return true;
            } else {
                for (let item of data) {
                    if (item[keyChildName] && item[keyChildName].length) {
                        if (isFoundChild(keyValue, item[keyChildName])) {
                            parents.push(item);
                            return true;
                        }
                    }
                }
                return false;
            }
        }

        const parents = [];

        if (arrayObjects.find(item => item[keyName] === keyValue)) {
            return [];
        } else {
            for (let item of arrayObjects) {
                if (item[keyChildName] && item[keyChildName].length) {
                    if (isFoundChild(keyValue, item[keyChildName], parents)) {
                        parents.push(item);
                        return parents;
                    }
                }
            }
        }

        return parents;
    }

    /**
     * Заменяем объект по значению свойства
     * @param arrayObjects
     * @param keyName
     * @param keyValue
     * @param needleObject
     * @return {Object[] | []}
     */
    static mergeObjectByParamValue(arrayObjects, keyName, keyValue, needleObject)
    {
        const itemIndex = arrayObjects.findIndex(item => item[keyName] === keyValue);
        if (itemIndex !== -1) {
            arrayObjects[itemIndex] = {...arrayObjects[itemIndex], ...needleObject};
        }
    }
}
